import { useEffect, useRef, useState } from "react";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { environment } from "environment/environment";
import AngelOneGreeksWebSocketException from "service/websocket/AngelOneGreeksWebSocketException";

const MAX_INTRADAY_POINTS = 390;

const asSnapshots = (payload) => {
  const values = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.snapshots)
      ? payload.snapshots
      : payload?.snapshot
        ? [payload.snapshot]
        : [payload];
  return values.filter((value) => value && typeof value === "object");
};

const snapshotKey = (snapshot) => [
  snapshot.accountName || "",
  snapshot.createdDate || "",
  snapshot.createdTime || "",
].join("|");

const snapshotOrder = (left, right) => {
  const leftDateTime = `${left.createdDate || ""} ${left.createdTime || ""}`;
  const rightDateTime = `${right.createdDate || ""} ${right.createdTime || ""}`;
  return leftDateTime.localeCompare(rightDateTime);
};

/**
 * Angel One Greeks-only browser stream.
 *
 * This is deliberately separate from NiftyWebsocketApi. The backend SmartStream
 * connection remains server-side; this client receives only the snapshots that
 * were calculated and saved by the Angel One Greeks service.
 */
const useAngelOneGreeksWebsocket = (
  socketUrl = environment.angelOneGreeksWebsocketUrl,
  topic = environment.angelOneGreeksTopic,
  historyTopic = environment.angelOneGreeksHistoryTopic,
  historyRequestDestination = environment.angelOneGreeksHistoryRequestDestination,
) => {
  const [history, setHistory] = useState([]);
  const [latest, setLatest] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const snapshotSources = useRef(new Map());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const stompClient = new Client({
      webSocketFactory: () => new SockJS(socketUrl),
      reconnectDelay: 5000,
      connectionTimeout: 10000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      discardWebsocketOnCommFailure: true,
      debug: () => {},
    });
    const handleMessage = (message, source) => {
      try {
        const payload = JSON.parse(message.body);
        const snapshots = asSnapshots(payload);
        if (!snapshots.length) return;

        setLatest(snapshots[snapshots.length - 1]);
        setHistory((currentHistory) => {
          const merged = new Map(currentHistory.map((snapshot) => [
            snapshotKey(snapshot), snapshot,
          ]));
          snapshots.forEach((snapshot) => {
            const key = snapshotKey(snapshot);
            const existingSource = snapshotSources.current.get(key);
            if (source === "live" || existingSource !== "live") {
              merged.set(key, snapshot);
              snapshotSources.current.set(key, source);
            }
          });
          const result = [...merged.values()].sort(snapshotOrder).slice(-MAX_INTRADAY_POINTS);
          const resultKeys = new Set(result.map(snapshotKey));
          snapshotSources.current.forEach((sourceValue, key) => {
            if (!resultKeys.has(key)) snapshotSources.current.delete(key);
          });
          return result;
        });
        if (source === "live") setLastUpdated(Date.now());
      } catch (cause) {
        const exception = new AngelOneGreeksWebSocketException(
          "Unable to read an Angel One Greeks websocket message",
          cause,
        );
        setError(exception);
        console.error(exception);
      }
    };

    stompClient.onConnect = () => {
      setIsConnected(true);
      setError(null);
      stompClient.subscribe(topic, (message) => {
        handleMessage(message, "live");
      });
      stompClient.subscribe(historyTopic, (message) => {
        handleMessage(message, "history");
      });
      stompClient.publish({
        destination: historyRequestDestination,
        body: JSON.stringify({ accountName: "PK", underlying: "NIFTY" }),
      });
    };

    stompClient.onStompError = (frame) => {
      const exception = new AngelOneGreeksWebSocketException(
        frame?.headers?.message || "Angel One Greeks broker error",
      );
      setError(exception);
      console.error(exception, frame?.body || "");
    };

    stompClient.onWebSocketError = (cause) => {
      setIsConnected(false);
      const exception = new AngelOneGreeksWebSocketException(
        "Angel One Greeks websocket connection error",
        cause,
      );
      setError(exception);
      console.error(exception);
    };

    stompClient.onWebSocketClose = (event) => {
      setIsConnected(false);
      if (!event?.wasClean) {
        console.warn(`Angel One Greeks websocket closed (${event?.code || "unknown"}); reconnecting`);
      }
    };

    stompClient.onDisconnect = () => {
      setIsConnected(false);
    };

    stompClient.activate();

    return () => {
      setIsConnected(false);
      if (stompClient.active) {
        void stompClient.deactivate().catch((cause) => {
          console.error(new AngelOneGreeksWebSocketException(
            "Unable to close Angel One Greeks websocket connection",
            cause,
          ));
        });
      }
    };
  }, [historyRequestDestination, historyTopic, socketUrl, topic]);

  return { history, latest, isConnected, lastUpdated, error };
};

export default useAngelOneGreeksWebsocket;
